"use client"

import { Bar, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartOptions,
} from "chart.js"
import { Box, FormControl, IconButton, MenuItem, Select, Typography } from "@mui/material"
import { FirstPage, LastPage, NavigateBefore, NavigateNext } from "@mui/icons-material"
import { formatNumber } from "../../utils/data"
import { useState, useEffect } from "react"

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend)

export type ChartType = "bar" | "scurve" | "line"
export type Period = "0" | "1" | "2"

interface TimeSeriesChartProps {
  title: string
  chartType: ChartType
  period: Period
  onChartTypeChange: (type: ChartType) => void
  onPeriodChange: (period: Period) => void
  data: {
    label: string
    value: number
  }[]
  sCurveData: {
    label: string
    value: number
  }[]
  color?: string
  selectHeight?: number
  onSelect?: (index: number) => void
  enablePagination?: boolean
  itemsPerPage?: number
}

export function TimeSeriesChart({
  title,
  chartType,
  period,
  onChartTypeChange,
  onPeriodChange,
  data,
  sCurveData,
  color = "rgba(239, 68, 68, 0.8)",
  selectHeight = 16,
  onSelect,
  enablePagination = false,
  itemsPerPage = 10,
}: TimeSeriesChartProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [startOffset, setStartOffset] = useState(0)

  const displayData = chartType === "scurve" ? sCurveData : data

  const totalItems = displayData.length
  const endOffset = Math.min(startOffset + itemsPerPage, totalItems)
  const paginatedData = displayData.slice(startOffset, endOffset)
  const showPagination = enablePagination && totalItems > itemsPerPage

  useEffect(() => {
    setStartOffset(0)
  }, [displayData.length])

  const handleNextItem = () => {
    if (startOffset + itemsPerPage < totalItems) {
      setStartOffset(startOffset + 1)
    }
  }

  const handlePrevItem = () => {
    if (startOffset > 0) {
      setStartOffset(startOffset - 1)
    }
  }

  const handleNext10Items = () => {
    const newOffset = Math.min(startOffset + 10, totalItems - itemsPerPage)
    setStartOffset(Math.max(0, newOffset))
  }

  const handlePrev10Items = () => {
    const newOffset = Math.max(startOffset - 10, 0)
    setStartOffset(newOffset)
  }

  const chartData = {
    labels: paginatedData.map((item) => item.label),
    datasets: [
      {
        data: paginatedData.map((item) => item.value),
        backgroundColor: paginatedData.map((_, index) => {
          const actualIndex = startOffset + index
          return onSelect && actualIndex === selectedIndex ? "rgba(20, 124, 157, 0.1)" : color
        }),
        borderColor: paginatedData.map((_, index) => {
          const actualIndex = startOffset + index
          return onSelect && actualIndex === selectedIndex ? "rgba(20, 124, 157, 1)" : color.replace("0.8", "1")
        }),
        borderWidth: 2,
        tension: chartType !== "line" ? 0.4 : 0,
        fill: false,
        pointBackgroundColor: color.replace("0.8", "1"),
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 3,
      },
    ],
  }

  const barOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (onSelect && elements.length > 0) {
        const actualIndex = startOffset + elements[0].index
        setSelectedIndex(actualIndex)
        onSelect(actualIndex)
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${formatNumber(context.parsed.y)}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          callback: (value) => formatNumber(Number(value)),
        },
      },
    },
  }

  const lineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (onSelect && elements.length > 0) {
        const actualIndex = startOffset + elements[0].index
        setSelectedIndex(actualIndex)
        onSelect(actualIndex)
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${formatNumber(context.parsed.y)}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          callback: (value) => formatNumber(Number(value)),
        },
      },
    },
  }

  const periodLabels = {
    "0": "Mensual",
    "1": "Semanal",
    "2": "Anual",
  }

  const chartTypeLabels = {
    bar: "Barra",
    scurve: "Curva S",
    line: "Líneas",
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={chartType}
              onChange={(e) => onChartTypeChange(e.target.value as ChartType)}
              sx={{
                height: `${selectHeight}px`,
                "& .MuiSelect-select": {
                  py: 0,
                  fontSize: "0.75rem",
                },
              }}
            >
              <MenuItem value="bar">{chartTypeLabels.bar}</MenuItem>
              <MenuItem value="scurve">{chartTypeLabels.scurve}</MenuItem>
              <MenuItem value="line">{chartTypeLabels.line}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={period}
              onChange={(e) => onPeriodChange(e.target.value as Period)}
              sx={{
                height: `${selectHeight}px`,
                "& .MuiSelect-select": {
                  py: 0,
                  fontSize: "0.75rem",
                },
              }}
            >
              <MenuItem value="0">{periodLabels["0"]}</MenuItem>
              <MenuItem value="1">{periodLabels["1"]}</MenuItem>
              <MenuItem value="2">{periodLabels["2"]}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {chartType === "bar" ? (
          <Bar data={chartData} options={barOptions} />
        ) : (
          <Line data={chartData} options={lineOptions} />
        )}
      </Box>

      {showPagination && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 2 }}>
          <IconButton size="small" onClick={handlePrev10Items} disabled={startOffset === 0} title="Retroceder 10 items">
            <FirstPage fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handlePrevItem} disabled={startOffset === 0} title="Retroceder 1 item">
            <NavigateBefore fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: "120px", textAlign: "center" }}>
            {startOffset + 1} - {endOffset} de {totalItems}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextItem}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 1 item"
          >
            <NavigateNext fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext10Items}
            disabled={startOffset + itemsPerPage >= totalItems}
            title="Avanzar 10 items"
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}
